// components/TipoTalonarioSelector.js
import React, { useEffect, useState } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box, 
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Container
} from '@mui/material';
import { UrlApi } from '../../../../services/apirest';

// Configuración de colores profesionales para cada tipo
const TIPO_CONFIG = {
  default: {
    color: '#1976d2',
    bgColor: '#e3f2fd',
    borderColor: '#90caf9',
    label: 'General'
  },
 
};

const getTipoConfig = (descripcion) => {
  if (!descripcion) return TIPO_CONFIG.default;
  const descLower = descripcion.toLowerCase();
  return TIPO_CONFIG.default;
};

const TipoTalonarioSelector = ({ onTipoChange, selectedTipo }) => {
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    obtenerTipos();
  }, []);

  const obtenerTipos = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${UrlApi}/envasado_tipos_talonario/activos`);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      const tiposActivos = data.filter(tipo => tipo.activo === 1);
      setTipos(tiposActivos);
      
      if (tiposActivos.length > 0 && !selectedTipo) {
        onTipoChange(tiposActivos[0].id);
      }
    } catch (error) {
      setError('Error al cargar los tipos de talonario');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ 
          minWidth: 280, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          px: 3,
          py: 1.5,
          bgcolor: '#fafafa',
          borderRadius: 1.5,
          border: '1px solid #e0e0e0'
        }}>
          <CircularProgress size={20} thickness={4} />
          <Typography variant="body2" color="textSecondary">
            Cargando tipos...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Alert 
          severity="error" 
          sx={{ 
            minWidth: 280, 
            borderRadius: 1.5
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (tipos.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Alert 
          severity="warning" 
          sx={{ 
            minWidth: 280, 
            borderRadius: 1.5
          }}
        >
          No hay tipos disponibles
        </Alert>
      </Container>
    );
  }

  const selectedConfig = getTipoConfig(
    tipos.find(t => t.id === selectedTipo)?.descripcion
  );

  return (
    <Container maxWidth="sm" sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ 
        width: '100%',
        maxWidth: 400,
        position: 'relative'
      }}>
        <FormControl fullWidth size="small">
          <InputLabel 
            id="tipo-talonario-label"
            sx={{
              fontWeight: 500,
              '&.Mui-focused': {
                color: selectedConfig.color
              }
            }}
          >
            Tipo de Talonario
          </InputLabel>
          <Select
            labelId="tipo-talonario-label"
            id="tipo-talonario-select"
            value={selectedTipo || ''}
            label="Tipo de Talonario"
            onChange={(e) => onTipoChange(e.target.value)}
            sx={{ 
              bgcolor: '#ffffff',
              borderRadius: 1.5,
              transition: 'all 0.2s ease',
              '& .MuiSelect-select': {
                py: 1.5,
                display: 'flex',
                alignItems: 'center'
              },
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: selectedConfig.color,
                  borderWidth: 2
                }
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: selectedConfig.color,
                borderWidth: 2,
                boxShadow: `0 0 0 3px ${selectedConfig.bgColor}`
              }
            }}
            renderValue={(value) => {
              const tipo = tipos.find(t => t.id === value);
              if (!tipo) return null;
              const config = getTipoConfig(tipo.descripcion);
              return (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  width: '100%'
                }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: config.color,
                      flexShrink: 0
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    fontWeight={500}
                    sx={{ color: config.color }}
                  >
                    {tipo.descripcion}
                  </Typography>
                  <Chip
                    // label={tipo.activo === 1 ? 'Activo' : 'Inactivo'}
                    size="small"
                    sx={{
                      ml: 'auto',
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: tipo.activo === 1 ? '#e8f5e9' : '#ffebee',
                      color: tipo.activo === 1 ? '#2e7d32' : '#c62828',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </Box>
              );
            }}
          >
            {tipos.map((tipo) => {
              const config = getTipoConfig(tipo.descripcion);
              const isSelected = selectedTipo === tipo.id;
              return (
                <MenuItem 
                  key={tipo.id} 
                  value={tipo.id}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderLeft: `3px solid transparent`,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: config.bgColor,
                      borderLeftColor: config.color
                    },
                    '&.Mui-selected': {
                      bgcolor: config.bgColor,
                      borderLeftColor: config.color,
                      '&:hover': {
                        bgcolor: config.bgColor
                      }
                    }
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    width: '100%'
                  }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: isSelected ? config.color : '#bdbdbd',
                        flexShrink: 0,
                        transition: 'all 0.2s ease'
                      }}
                    />
                    <Typography 
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? config.color : 'inherit'
                      }}
                    >
                      {tipo.descripcion}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
    </Container>
  );
};

export default TipoTalonarioSelector;